import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePsychologistData } from "@/hooks/usePsychologistData";
import { FERIADOS_CHANGED_EVENT, loadFeriadosTrabaja, setFeriadoTrabaja } from "@/utils/holidayDecisions";
import { timezoneToCountryCode } from "@/utils/timezoneToCountry";
import {
  countryNameFromTimezone,
  fetchPublicHolidays,
  HOLIDAY_NOTICE_DAYS,
  holidaysWithinDays,
} from "@/utils/holidays";
import { isHolidayFullyBlocked, sessionsOnHoliday } from "@/pages/psychologist/tabs/comunidad/holidaySessions";
import type { OccupyingSession } from "@/pages/psychologist/tabs/comunidad/formacionBlock";

export interface HolidayNotice {
  /** YYYY-MM-DD, el día del feriado en la zona horaria de la psicóloga. */
  date: string;
  name: string;
  /** 1 = no bloqueó y no tiene sesiones · 2 = tiene sesiones ese día. (El 3 no se devuelve.) */
  state: 1 | 2;
  sessions: OccupyingSession[];
  /** Dijo "Voy a trabajar": el aviso se achica. */
  trabaja: boolean;
}

/**
 * Aviso de feriados de la Agenda: feriados del país de la psicóloga en los próximos 14 días
 * (incluido el mismo día), cada uno con su estado:
 *   1. no bloqueó y no tiene sesiones → invitarla a bloquear
 *   2. tiene sesiones (no canceladas) ese día → cuántos y cuáles pacientes
 *   3. ya bloqueó el día completo y no tiene sesiones → no se devuelve
 * Todo en SU zona horaria. Los feriados salen de la misma consulta que el aviso de Comunidad.
 * Si algo falla, ese feriado no se muestra (nunca un aviso a medias).
 */
export function useHolidayNotices() {
  const psychologist = usePsychologistData();
  const timezone =
    psychologist?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Buenos_Aires";
  const location = useLocation();
  const [notices, setNotices] = useState<HolidayNotice[]>([]);
  const [loading, setLoading] = useState(true);
  // Se recalcula si cambia "Voy a trabajar" desde otro lado (la marca del feriado en el día).
  const [changeTick, setChangeTick] = useState(0);
  useEffect(() => {
    const onChange = () => setChangeTick((t) => t + 1);
    window.addEventListener(FERIADOS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(FERIADOS_CHANGED_EVENT, onChange);
  }, []);

  // Se recalcula al volver de bloquear (cambia la URL al cerrar el panel de bloqueo).
  const refreshKey = `${location.pathname}?${new URLSearchParams(location.search).get("config") ?? ""}`;

  useEffect(() => {
    let cancelled = false;
    const country = timezoneToCountryCode(timezone);
    if (!country) {
      setNotices([]);
      setLoading(false);
      return;
    }
    (async () => {
      const holidays = holidaysWithinDays(await fetchPublicHolidays(country), HOLIDAY_NOTICE_DAYS, timezone);
      if (holidays.length === 0) {
        if (!cancelled) {
          setNotices([]);
          setLoading(false);
        }
        return;
      }
      const trabaja = await loadFeriadosTrabaja();
      const results = await Promise.all(
        holidays.map(async (h): Promise<HolidayNotice | null> => {
          try {
            const sessions = await sessionsOnHoliday(h.date, timezone);
            if (sessions.length > 0)
              return { date: h.date, name: h.localName, state: 2, sessions, trabaja: trabaja.has(h.date) };
            if (await isHolidayFullyBlocked(h.date, timezone)) return null;
            return { date: h.date, name: h.localName, state: 1, sessions: [], trabaja: trabaja.has(h.date) };
          } catch {
            return null;
          }
        }),
      );
      if (!cancelled) {
        setNotices(results.filter((n): n is HolidayNotice => n !== null));
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) {
        setNotices([]);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [timezone, refreshKey, changeTick]);

  /** "Voy a trabajar". Impersonando no se guarda (soporte no apaga el aviso de la psicóloga). */
  const marcarTrabajo = useCallback(async (date: string) => {
    setNotices((prev) => prev.map((n) => (n.date === date ? { ...n, trabaja: true } : n)));
    await setFeriadoTrabaja(date, true);
  }, []);

  return {
    notices,
    loading,
    timezone,
    countryName: countryNameFromTimezone(timezone) ?? "tu país",
    marcarTrabajo,
  };
}
